import React from "react";
import { Link } from "react-router-dom";
import { Plus, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/LanguageContext";
import { adminHref } from "../adminPath";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import FieldError, { invalidInputClass } from "../../components/ui/FieldError";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import { currencyLabel } from "../../lib/currencies";
import {
  DESCRIPTION_MAX,
  OBJECTIVE_MAX,
  OBJECTIVES_MAX,
  PATH_TYPES,
  TITLE_MAX,
  switchMergesLevels,
} from "../../pathways/builderModel";
import { typeHint, typeLabel } from "./labels";

/** DOM ids the activation banner scrolls to (see PathwayBuilder.jsx). */
export const BASICS_ANCHORS = Object.freeze({
  objectives: "pathway-basics-objectives",
  dates: "pathway-basics-dates",
});

const fieldLabel = "block text-sm text-eh-muted mb-1.5";

/**
 * Builder step 1, Basics (spec screen 2, contract §2), in the wireframe's order: Name;
 * Description (shown on /parcours); Objectives (1 required to activate, max 6); the
 * Certificate toggle; the Path type, three cards with a one-line explanation each and the
 * "locked once Active with members" note; the Schedule (cohort with its dates, or
 * self-paced); Capacity; Price (0 = free).
 *
 * THE TYPE LOCK is the server's (`AdminPathway.path_type_locked`: published with at least
 * one learner). While it holds, the three cards are disabled.
 *
 * GOING FLAT MERGES LEVELS. A `level` pathway with several levels switched to Successive or
 * Alternative must become one level (contract §2), so the author is asked first; on "yes"
 * the levels are merged in order (builderModel.setPathType), on "no" nothing changes.
 *
 * THE PRICE is typed in the practice's currency, which is stated, not chosen (the
 * catalogue convention, lib/useTenantCurrency.js): the input is labelled with the code.
 *
 * @param {object}  props
 * @param {object}  props.builder     usePathwayBuilder() result
 * @param {boolean} props.disabled    read-only (plan gate, or an archived pathway)
 * @param {boolean} props.typeLocked  AdminPathway.path_type_locked
 * @param {string|null} props.currency  the pathway's currency, else the practice's; null = not known yet
 */
export default function BasicsPanel({ builder, disabled, typeLocked, currency }) {
  const t = useT("admin");
  const { draft, errors } = builder;
  const err = (key) => (errors[key] ? t(errors[key]) : "");

  const setObjective = (i, value) => builder.setField("objectives", draft.objectives.map((o, j) => (j === i ? value : o)));
  const removeObjective = (i) => {
    const next = draft.objectives.filter((_, j) => j !== i);
    builder.setField("objectives", next.length > 0 ? next : [""]);
  };
  const addObjective = () => builder.setField("objectives", [...draft.objectives, ""]);

  const chooseType = (type) => {
    if (type === draft.path_type) return;
    if (switchMergesLevels(draft, type)
      && !window.confirm(t("path.basics.mergeConfirm", { n: draft.levels.length, type: typeLabel(t, type) }))) return;
    builder.setPathType(type);
  };

  const cohort = draft.schedule === "cohort";
  return (
    <div data-testid={IDS.basics} className="bg-white rounded-3xl border border-eh-border-soft p-5 sm:p-6 space-y-6">
      <div>
        <label htmlFor="pathway-name" className={fieldLabel}>{t("path.basics.name")}</label>
        <Input
          id="pathway-name"
          value={draft.title}
          onChange={(e) => builder.setField("title", e.target.value)}
          maxLength={TITLE_MAX}
          disabled={disabled}
          placeholder={t("path.basics.namePh")}
          data-testid={IDS.name}
          aria-invalid={Boolean(errors.title)}
          className={errors.title ? invalidInputClass : ""}
        />
        <FieldError message={err("title")} />
      </div>

      <div>
        <label htmlFor="pathway-description" className={fieldLabel}>{t("path.basics.description")}</label>
        <textarea
          id="pathway-description"
          value={draft.description}
          onChange={(e) => builder.setField("description", e.target.value)}
          rows={4}
          maxLength={DESCRIPTION_MAX}
          disabled={disabled}
          data-testid={IDS.description}
          aria-invalid={Boolean(errors.description)}
          className={`w-full border border-input rounded-md px-3 py-2 text-sm bg-white resize-y ${errors.description ? invalidInputClass : ""}`}
        />
        <FieldError message={err("description")} />
      </div>

      <fieldset id={BASICS_ANCHORS.objectives} tabIndex={-1} className="outline-none">
        <legend className={fieldLabel}>{t("path.basics.objectives")}</legend>
        <div className="grid gap-2 sm:grid-cols-2">
          {draft.objectives.map((value, i) => (
            // Position IS identity: objectives are plain sentences without ids.
            <div key={i} className="flex items-center gap-1.5">
              <Input
                value={value}
                onChange={(e) => setObjective(i, e.target.value)}
                maxLength={OBJECTIVE_MAX}
                disabled={disabled}
                placeholder={t("path.basics.objectivePh")}
                aria-label={t("path.basics.objectiveN", { n: i + 1 })}
                data-testid={IDS.objective(i)}
              />
              {draft.objectives.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeObjective(i)}
                  disabled={disabled}
                  aria-label={t("path.basics.objectiveRemove", { n: i + 1 })}
                  title={t("path.basics.objectiveRemove", { n: i + 1 })}
                  data-testid={IDS.objectiveRemove(i)}
                  className="p-2 rounded-full text-eh-muted hover:text-rose-600 hover:bg-rose-50 disabled:opacity-30"
                >
                  <X className="w-4 h-4" aria-hidden="true" />
                </button>
              )}
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={addObjective}
          disabled={disabled || draft.objectives.length >= OBJECTIVES_MAX}
          data-testid={IDS.objectiveAdd}
          className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-eh-ochre-deep hover:text-eh-ink disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus className="w-4 h-4" aria-hidden="true" /> {t("path.basics.objectiveAdd")}
        </button>
        <FieldError message={err("objectives")} />
      </fieldset>

      <div className="flex items-center justify-between gap-4 rounded-2xl bg-eh-cream/70 border border-eh-border-soft px-4 py-3">
        <div>
          <p id="pathway-certificate-label" className="text-sm font-semibold text-eh-ink">{t("path.basics.certificate")}</p>
          <p id="pathway-certificate-hint" className="text-sm text-eh-muted">{t("path.basics.certificateHint")}</p>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={draft.certificate_enabled}
          aria-labelledby="pathway-certificate-label"
          aria-describedby="pathway-certificate-hint"
          disabled={disabled}
          onClick={() => builder.setField("certificate_enabled", !draft.certificate_enabled)}
          data-testid={IDS.certificate}
          className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full transition disabled:opacity-50 ${
            draft.certificate_enabled ? "bg-eh-sage-deep" : "bg-eh-border"
          }`}
        >
          <span
            aria-hidden="true"
            className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-all ${draft.certificate_enabled ? "left-6" : "left-1"}`}
          />
        </button>
      </div>

      {draft.certificate_enabled && (
        <div className="flex items-center justify-between text-xs text-eh-muted bg-white/70 border border-eh-border-soft rounded-xl px-4 py-2.5">
          <span>{t("brand.certificateSubtitle")}</span>
          <Link
            to={adminHref("/admin/branding")}
            className="font-medium text-eh-ink underline underline-offset-2 hover:text-sol-gold transition ml-2 shrink-0"
          >
            {t("brand.certificateCustomizeLink")}
          </Link>
        </div>
      )}

      <fieldset>
        <legend className="text-sm font-semibold text-eh-ink mb-2">{t("path.basics.pathType")}</legend>
        <div className="space-y-2" role="radiogroup" aria-describedby="pathway-type-locked">
          {PATH_TYPES.map((type) => {
            const checked = draft.path_type === type;
            return (
              <label
                key={type}
                className={`block rounded-2xl border px-4 py-3 cursor-pointer transition ${
                  checked ? "border-eh-ochre-deep bg-eh-ochre/10" : "border-eh-border-soft bg-eh-cream/40 hover:border-eh-border"
                } ${disabled || typeLocked ? "cursor-not-allowed opacity-70" : ""}`}
              >
                <span className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="pathway-path-type"
                    value={type}
                    checked={checked}
                    onChange={() => chooseType(type)}
                    disabled={disabled || typeLocked}
                    data-testid={IDS.pathType(type)}
                    className="accent-eh-ochre-deep"
                  />
                  <span className="text-sm font-semibold text-eh-ink">{typeLabel(t, type)}</span>
                </span>
                <span className="block text-sm text-eh-muted ml-6">{typeHint(t, type)}</span>
              </label>
            );
          })}
        </div>
        <p
          id="pathway-type-locked"
          data-testid={IDS.pathTypeLockedNote}
          data-locked={typeLocked ? "true" : "false"}
          className={`mt-2 text-sm ${typeLocked ? "text-amber-800" : "text-eh-muted"}`}
        >
          {typeLocked ? t("path.basics.typeLockedNow") : t("path.basics.typeLockNote")}
        </p>
      </fieldset>

      <fieldset id={BASICS_ANCHORS.dates} tabIndex={-1} className="outline-none">
        <legend className="text-sm font-semibold text-eh-ink mb-2">{t("path.basics.schedule")}</legend>
        <div className="flex flex-wrap items-center gap-5 text-sm text-eh-ink" role="radiogroup">
          {["cohort", "self_paced"].map((value) => (
            <label key={value} className="inline-flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="pathway-schedule"
                value={value}
                checked={draft.schedule === value}
                onChange={() => builder.setField("schedule", value)}
                disabled={disabled}
                data-testid={IDS.schedule(value)}
                className="accent-eh-ochre-deep"
              />
              {t(`path.basics.schedule.${value}`)}
            </label>
          ))}
        </div>
        <p className="mt-1 text-sm text-eh-muted">{t(cohort ? "path.basics.cohortHint" : "path.basics.selfPacedHint")}</p>
        {cohort && (
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="pathway-start-date" className={fieldLabel}>{t("path.basics.startDate")}</label>
              <Input
                id="pathway-start-date"
                type="date"
                value={draft.start_date}
                onChange={(e) => builder.setField("start_date", e.target.value)}
                disabled={disabled}
                data-testid={IDS.startDate}
                aria-invalid={Boolean(errors.dates)}
                className={errors.dates ? invalidInputClass : ""}
              />
            </div>
            <div>
              <label htmlFor="pathway-end-date" className={fieldLabel}>{t("path.basics.endDate")}</label>
              <Input
                id="pathway-end-date"
                type="date"
                value={draft.end_date}
                min={draft.start_date || undefined}
                onChange={(e) => builder.setField("end_date", e.target.value)}
                disabled={disabled}
                data-testid={IDS.endDate}
                aria-invalid={Boolean(errors.dates)}
                className={errors.dates ? invalidInputClass : ""}
              />
            </div>
          </div>
        )}
        <FieldError message={err("dates")} />
      </fieldset>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="pathway-capacity" className={fieldLabel}>{t("path.basics.capacity")}</label>
          <Input
            id="pathway-capacity"
            type="number"
            inputMode="numeric"
            min="1"
            value={draft.capacity}
            onChange={(e) => builder.setField("capacity", e.target.value)}
            disabled={disabled}
            placeholder={t("path.basics.capacityPh")}
            data-testid={IDS.capacity}
            aria-invalid={Boolean(errors.capacity)}
            className={errors.capacity ? invalidInputClass : ""}
          />
          <FieldError message={err("capacity")} />
        </div>
        <div>
          <label htmlFor="pathway-price" className={fieldLabel}>{t("path.basics.price")}</label>
          <div className="flex items-center gap-2">
            <span data-testid={IDS.priceCurrency} className="text-sm font-semibold text-eh-ink min-w-[2.5rem]">
              {currencyLabel(currency)}
            </span>
            <Input
              id="pathway-price"
              inputMode="decimal"
              value={draft.price}
              onChange={(e) => builder.setField("price", e.target.value)}
              disabled={disabled}
              data-testid={IDS.price}
              aria-invalid={Boolean(errors.price)}
              className={errors.price ? invalidInputClass : ""}
            />
          </div>
          <FieldError message={err("price")} />
        </div>
      </div>
    </div>
  );
}
